import { Link } from 'react-router-dom';
import { ArrowRightIcon, GiftIcon } from 'lucide-react';
import { ProductCard } from '../shared/ProductCard';
import { HeadingText } from '../shared/HeadingText';
import { Rail } from '../shared/Rail';
import { usePages, useSite } from '../../contexts/SiteContext';
import { euro } from '../../core/format/currency';
import { fill } from '../../utils/copy';
import { quietLink, sectionHeading } from '../../ui/sections';
import { Section } from '../shared/Section';
import type { Product } from '../../core/types/catalog';
type Props = {
  products: Product[];
  total: number;
};

/**
 * "Liever met één product beginnen?": de lage drempel na het complete plan. Bestsellers en
 * salonfavorieten op de rail; de kaarten komen kort na elkaar binnen.
 *
 * Daaronder de cadeaustaffel. Die geldt voor elke bestelling, ook voor één product, dus hoort
 * hij hier: wie klein begint, ziet meteen wat er bij een iets grotere bestelling bij komt.
 */
export function HomeShelf({
  products,
  total
}: Props) {
  const pages = usePages();
  const {
    giftTiers
  } = useSite();
  const copy = pages.home.bestsellers;
  return <Section tone="sand" labelledBy="home-bestsellers" contained={false} data-id="element-7777" data-mp="2067q4qpal0au">
      <div className="mx-auto max-w-content px-6 lg:px-10" data-id="element-7778" data-mp="1v74ozhcfne85">
        <div className="flex flex-wrap items-end justify-between gap-6" data-id="element-7779" data-mp="1v5jutsu15xat">
          <div className="max-w-2xl" data-id="element-7780" data-mp="1q7i91057zjvn">
            <h2 id="home-bestsellers" className={`${sectionHeading} text-ink`} data-id="element-7781" data-mp="1qanywhloapsf">
              <HeadingText heading={copy.heading} data-id="element-7782" data-mp="1lai4wurxkr76" />
            </h2>
            {copy.body && <p className="mt-5 max-w-xl text-[17px] leading-relaxed text-muted" data-id="element-7783" data-mp="1ghp0k80djkvm">{fill(copy.body)}</p>}
          </div>
          {copy.cta && <Link to="/webshop" className={`${quietLink} text-ink`} data-id="element-7784" data-mp="17hshclwr7e7y">
              {fill(copy.cta, {
            count: total
          })}
              <ArrowRightIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-7785" data-mp="12j8tuh2ihudh" />
            </Link>}
        </div>

        <Rail id="home-shelf" label="Salonfavorieten" className="mt-10 lg:mt-12" stagger items={products} itemKey={p => p.id} itemClassName="w-[68%] sm:w-[40%] lg:w-[calc((100%-4.5rem)/3.5)]" renderItem={p => <div className="h-full w-full transition-[transform,box-shadow] duration-150 ease-soft [@media(hover:hover)]:hover:-translate-y-1 [@media(hover:hover)]:hover:shadow-lift" data-id="element-7787" data-mp="ewnbwvcgrekh">
              <ProductCard product={p} data-id="element-7788" data-mp="16nje920we0ti" />
            </div>} data-id="element-7786" data-mp="xkxh9yku15rk" />

        {/* De staffel als één rij: bedrag en cadeau naast elkaar (telefoon: onder elkaar). */}
        {copy.giftsTitle && giftTiers.length > 0 && <div className="mt-14 border-t border-ink/15 pt-8 lg:mt-16" data-id="element-7789" data-mp="xty09kwk4nl5">
            <h3 className="flex items-center gap-2.5 text-[15px] text-ink" data-id="element-7790" data-mp="11uqaohh60yj0">
              <GiftIcon className="h-4 w-4 text-clay" strokeWidth={1.5} aria-hidden="true" data-id="element-7791" data-mp="1pkudwnqfyw9h" />
              {fill(copy.giftsTitle)}
            </h3>
            <ul aria-label="Cadeaus bij je bestelling" className={`mt-5 grid gap-px bg-ink/10 sm:grid-cols-2 ${giftTiers.length >= 3 ? 'lg:grid-cols-3' : ''} ${giftTiers.length >= 4 ? 'xl:grid-cols-4' : ''}`} data-id="element-7792" data-mp="1uddqbhde7lcp">
              {giftTiers.map(tier => <li key={tier.threshold} className="flex gap-4 bg-sand py-4 sm:px-5 sm:first:pl-0" data-id="element-7793" data-mp="3orrnbd33qyh6">
                  <span className="w-16 shrink-0 type-name text-clay" data-id="element-7794" data-mp="3jtq0ch2k2f0f">
                    <span className="sr-only" data-id="element-7795" data-mp="2w4oqcpe2qvt1">Vanaf </span>
                    {euro(tier.threshold)}
                  </span>
                  <span className="text-[14px] leading-snug text-muted" data-id="element-7796" data-mp="2r63roueh0k6p">
                    <span className="block text-ink" data-id="element-7797" data-mp="362uoqx0o9jt5">{tier.title}</span>
                    {tier.description}
                  </span>
                </li>)}
            </ul>
          </div>}
      </div>
    </Section>;
}